import { useState, useEffect, useRef } from "react";
import "./style.css";
const { DateTime } = require("luxon");

const formatDate = (dateStr) =>
  dateStr ? DateTime.fromISO(dateStr).plus({ hours: 3 }).toFormat("dd/MM/yyyy") : "";

const formatDateTime = (dateStr) =>
  dateStr ? DateTime.fromISO(dateStr).plus({ hours: 3 }).toFormat("dd/MM/yyyy HH:mm:ss") : "";

const formatFloat = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(parseFloat(value));
};

const Table = ({
  columns,
  data,
  onEdit,
  onDelete,
  menuActions,
  columnTextColors = {},
  isEditDisabled = () => false,
  isDeleteDisabled = () => false,
  extraIcons = [],
  showFooter = false,
  enableSelection = false,
  selectedItems = [],
  onSelectionChange = () => { },
  selectionKey = null,
  onRowClick,
  selectedRow
}) => {
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // --- Cálculo de totais ---
  const totals = {};
  if (showFooter) {
    columns.forEach((col) => {
      if (col.type === "float" || col.type === "integer") {
        totals[col.key] = data.reduce((acc, item) => {
          const val = parseFloat(item[col.key]);
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
      }
    });
  }

  // --- Seleção ---
  const getKeyValue = (item) => {
    if (!selectionKey) return JSON.stringify(item);
    return item[selectionKey];
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) onSelectionChange([...data]);
    else onSelectionChange([]);
  };

  const handleSelectOne = (item) => {
    const key = getKeyValue(item);
    const isSelected = selectedItems.some((i) => getKeyValue(i) === key);
    if (isSelected)
      onSelectionChange(selectedItems.filter((i) => getKeyValue(i) !== key));
    else onSelectionChange([...selectedItems, item]);
  };

  const isAllSelected =
    data.length > 0 &&
    data.every((item) =>
      selectedItems.some((i) => getKeyValue(i) === getKeyValue(item))
    );

  // --- Menu ---
  const toggleMenu = (index, e) => {
    e.stopPropagation();
    if (openMenuIndex === index) return setOpenMenuIndex(null);
    const rect = e.currentTarget.getBoundingClientRect();
    buttonRef.current = e.currentTarget;
    setOpenMenuIndex(index);

    setTimeout(() => {
      if (menuRef.current) {
        const menuHeight = menuRef.current.offsetHeight;
        const menuWidth = menuRef.current.offsetWidth;
        let y = rect.bottom + 4;
        let x = rect.left;
        const spaceBottom = window.innerHeight - rect.bottom;
        const spaceTop = rect.top;
        const spaceRight = window.innerWidth - rect.right;
        if (spaceBottom < menuHeight && spaceTop > menuHeight) {
          y = rect.top - menuHeight + rect.height - 4;
        }
        if (spaceRight < menuWidth) {
          x = window.innerWidth - menuWidth - 4;
        }
        setMenuPosition({
          top: y + window.scrollY,
          left: x + window.scrollX,
        });
      }
    }, 0);
  };

  const closeMenu = () => setOpenMenuIndex(null);

  useEffect(() => {
    const handleResizeOrScroll = () => {
      if (openMenuIndex !== null && buttonRef.current) {
        const event = { currentTarget: buttonRef.current, stopPropagation: () => { } };
        toggleMenu(openMenuIndex, event);
      }
    };
    window.addEventListener("resize", handleResizeOrScroll);
    window.addEventListener("scroll", handleResizeOrScroll);
    document.addEventListener("click", closeMenu);
    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll);
      document.removeEventListener("click", closeMenu);
    };
  }, [openMenuIndex]);

  // --- Renderização ---
  return (
    <>
      <hr />
      <div className="table-wrapper flex-grow-1 d-flex flex-column">
        <div className="table-responsive flex-grow-1 d-flex flex-column">
          <table className="table table-striped table-hover mb-0">
            <thead>
              <tr>
                {enableSelection && (
                  <th style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}

                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={
                      column.responsive
                        ? `d-none d-${column.responsive}-table-cell`
                        : ""
                    }
                  >
                    {column.title}
                  </th>
                ))}

                {extraIcons.length > 0 && <th></th>}
                {(onEdit ||
                  onDelete ||
                  (menuActions &&
                    (typeof menuActions === "function" ||
                      (Array.isArray(menuActions) && menuActions.length > 0)))) && (
                    <th>Ações</th>
                  )}
              </tr>
            </thead>

            <tbody>
              {data.map((item, index) => {
                const hoje = DateTime.local().startOf("day");
                const naoBaixado = !item.dtbaixa;
                let isHoje = false;
                let isVencido = false;

                const isSelectedRow =
                  selectedRow &&
                  item.codmoeda === selectedRow;

                if (item.dtvencimento && naoBaixado) {
                  const vencimento = DateTime.fromISO(item.dtvencimento).plus({ hours: 3 });
                  isHoje = vencimento.toISODate() === hoje.toISODate();
                  isVencido = vencimento < hoje && !isHoje;
                }

                const rowStyle = isHoje
                  ? { backgroundColor: "#fdf916ff" }
                  : isVencido
                    ? { backgroundColor: "#f8d7da" }
                    : {};

                const key = getKeyValue(item);
                const isSelected = selectedItems.some(
                  (i) => getKeyValue(i) === key
                );

                // 🔹 Suporte a menu dinâmico por item
                const resolvedMenuActions =
                  typeof menuActions === "function"
                    ? menuActions(item)
                    : menuActions || [];

                return (
                  <tr
                    key={index}
                    style={rowStyle}
                    onClick={() => onRowClick?.(item)}
                    style={{
                      ...rowStyle,
                      cursor: onRowClick ? "pointer" : "default",
                      backgroundColor: isSelected ? "#dbeafe" : rowStyle.backgroundColor
                    }}
                  >
                    {enableSelection && (
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectOne(item)}
                        />
                      </td>
                    )}

                    {columns.map((column) => {
                      const rawValue = item[column.key];
                      let value =
                        column.type === "date"
                          ? formatDate(rawValue)
                          : column.type === "datetime"
                            ? formatDateTime(rawValue)
                            : column.type === "float"
                              ? formatFloat(rawValue)
                              : rawValue;

                      if (column.maxLength && typeof value === "string") {
                        value =
                          value.length > column.maxLength
                            ? value.substring(0, column.maxLength) + "..."
                            : value;
                      }

                      let textColor = "inherit";
                      const colorDef = columnTextColors[column.key];
                      if (typeof colorDef === "function")
                        textColor = colorDef(rawValue);
                      else if (typeof colorDef === "string")
                        textColor = colorDef;

                      return (
                        <td
                          key={`${key}-${column.key}`}
                          style={{ color: textColor }}
                          className={
                            column.responsive
                              ? `d-none d-${column.responsive}-table-cell`
                              : ""
                          }
                        >
                          {value}
                        </td>
                      );
                    })}

                    {extraIcons.length > 0 && (
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {extraIcons.map((iconDef, i) =>
                            item[iconDef.field] === "true" ? (
                              <i key={i} className={iconDef.icon} style={{ fontSize: "1.5rem" }}></i>
                            ) : null
                          )}
                        </div>
                      </td>
                    )}

                    {(onEdit ||
                      onDelete ||
                      (resolvedMenuActions && resolvedMenuActions.length > 0)) && (
                        <td>
                          <div className="d-flex align-items-center">
                            {onEdit && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(item);
                                }}
                              >
                                {isEditDisabled(item) ? (
                                  <i className="fas fa-eye"></i>
                                ) : (
                                  <i className="fas fa-edit"></i>
                                )}
                              </button>
                            )}
                            {onDelete && !isDeleteDisabled(item) && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger me-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(item);
                                }}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                            {resolvedMenuActions &&
                              resolvedMenuActions.length > 0 && (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={(e) => toggleMenu(index, e)}
                                  >
                                    <i className="fas fa-ellipsis-v"></i>
                                  </button>
                                  {openMenuIndex === index && (
                                    <div
                                      ref={menuRef}
                                      className="dropdown-menu show position-absolute shadow rounded"
                                      style={{
                                        display: "block",
                                        top: `${menuPosition.top}px`,
                                        left: `${menuPosition.left}px`,
                                        minWidth: "150px",
                                        padding: "0.5rem 0",
                                        border: "1px solid rgba(0,0,0,0.15)",
                                        backgroundColor: "#fff",
                                        zIndex: 9999,
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {resolvedMenuActions.map(
                                        (action, actionIndex) => (
                                          <button
                                            key={actionIndex}
                                            className="dropdown-item text-dark d-flex align-items-center gap-2 px-3 py-2"
                                            style={{
                                              fontSize: "14px",
                                              whiteSpace: "nowrap",
                                            }}
                                            onClick={() => {
                                              action.onClick(item);
                                              closeMenu();
                                            }}
                                          >
                                            {action.icon && (
                                              <i className={action.icon}></i>
                                            )}
                                            {action.label}
                                          </button>
                                        )
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                          </div>
                        </td>
                      )}
                  </tr>
                );
              })}
            </tbody>

            {showFooter && (
              <tfoot>
                <tr style={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                  {enableSelection && <td></td>}
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.type === "float" ? formatFloat(totals[col.key]) : col.type === "integer" ? formatFloat(totals[col.key]) : ""}
                    </td>
                  ))}
                  {extraIcons.length > 0 && <td></td>}
                  {(onEdit ||
                    onDelete ||
                    (menuActions &&
                      (typeof menuActions === "function" ||
                        (Array.isArray(menuActions) &&
                          menuActions.length > 0)))) && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
};

export default Table;
